import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, Search, RefreshCw, User, FileText, Eye, Check, X, 
  Clock, Send, Loader2, FlaskConical, Shield, Printer, Filter
} from 'lucide-react';
import { labResultsAPI } from '../../services/api';
import ResultsDetailsModal from '../../components/lab/ResultsDetailsModal';
import toast from 'react-hot-toast';

const ApproveResults = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;
  
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  const statusOptions = [
    { value: '', label: 'جميع الحالات' },
    { value: 'completed', label: 'مكتمل - جاهز للاعتماد' },
    { value: 'approved', label: 'معتمد - جاهز للإرسال' },
    { value: 'released', label: 'تم الإرسال' }
  ];

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter || undefined,
        page: currentPage,
        limit: itemsPerPage,
        sort_order: sortOrder,
        group_by: 'booking',
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchQuery) params.search = searchQuery;
      
      const response = await labResultsAPI.getAll(params);
      const data = response.data?.data || {};
      setResults(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (err) {
      console.error('Error fetching results:', err);
      toast.error('حدث خطأ في تحميل النتائج');
      setResults([]);
      setTotalPages(1);
      setTotalItems(0);
    }
    setLoading(false);
  }, [statusFilter, dateFrom, dateTo, currentPage, sortOrder, searchQuery]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const handleOpenView = async (booking) => {
    try {
      const response = await labResultsAPI.getBookingResults(booking.booking_id);
      const data = response.data?.data;
      
    setViewData({
        booking: data.booking || {},
        tests: data.tests || []
    });
    setShowViewModal(true);
    } catch (err) {
      console.error('Error loading booking details:', err);
      toast.error('حدث خطأ في تحميل تفاصيل الحجز');
    }
  };

  const toggleSelection = (bookingId) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const selectAll = () => {
    const eligibleBookings = results
      .filter(r => r.status === 'completed' || r.status === 'approved')
      .map(r => r.booking_id);
    setSelectedBookings(eligibleBookings);
  };

  const clearSelection = () => {
    setSelectedBookings([]);
  };

  const handleApprove = async () => {
    if (selectedBookings.length === 0) {
      toast.error('يرجى اختيار حجوزات للاعتماد');
      return;
    }
    
    setProcessing(true);
    try {
      await labResultsAPI.approve(selectedBookings);
      toast.success(`تم اعتماد ${selectedBookings.length} حجز بنجاح`);
      setSelectedBookings([]);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الاعتماد');
    }
    setProcessing(false);
  };

  const handleRelease = async () => {
    if (selectedBookings.length === 0) {
      toast.error('يرجى اختيار حجوزات للإرسال');
      return;
    }
    
    setProcessing(true);
    try {
      await labResultsAPI.release(selectedBookings);
      toast.success(`تم إرسال ${selectedBookings.length} حجز بنجاح`);
      setSelectedBookings([]);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الإرسال');
    }
    setProcessing(false);
  };

  const handleSingleAction = async (bookingId, action) => {
    setProcessing(true);
    try {
      if (action === 'approve') {
        await labResultsAPI.approve([bookingId]);
        toast.success('تم اعتماد الحجز بنجاح');
      } else {
        await labResultsAPI.release([bookingId]);
        toast.success('تم إرسال الحجز بنجاح');
      }
      setSelectedBookings([]);
      fetchResults();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء تنفيذ العملية');
    }
    setProcessing(false);
  };

  const statusConfig = {
    pending: { label: 'في الانتظار', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: Clock },
    completed: { label: 'جاهز للاعتماد', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500', icon: Check },
    approved: { label: 'جاهز للإرسال', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: Shield },
    released: { label: 'تم الإرسال', bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500', icon: Send }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide ${config.bg} ${config.text} border ${config.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    );
  };

  const getPatientName = (booking) => {
    return booking.patient_name || '-';
  };

  const completedCount = results.filter(r => r.status === 'completed').length;
  const approvedCount = results.filter(r => r.status === 'approved').length;

  const hasActiveFilters = statusFilter || dateFrom || dateTo || sortOrder !== 'asc';
  const visiblePages = (() => {
    const total = Math.max(totalPages, 1);
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(total, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  })();

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">اعتماد وإرسال النتائج</h1>
              <p className="text-slate-500 text-sm">مراجعة واعتماد النتائج قبل إرسالها للعملاء</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-3">
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-2 text-center min-w-[100px]">
              <p className="text-2xl font-bold text-sky-700 tabular-nums">{completedCount}</p>
              <p className="text-[10px] font-semibold text-sky-600 uppercase tracking-wider">جاهز للاعتماد</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 text-center min-w-[100px]">
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">{approvedCount}</p>
              <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">جاهز للإرسال</p>
            </div>
          </div>
        </motion.div>

        {/* Search & Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] mb-6"
        >
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="البحث بالباركود أو اسم المريض..."
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all appearance-none cursor-pointer min-w-[160px]"
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  showFilters || hasActiveFilters
                    ? 'bg-[#1E293B] text-slate-100'
                    : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                }`}
              >
                <Filter className="w-4 h-4 text-blue-600" strokeWidth={2} />
                <span className="hidden sm:inline">فلترة</span>
              </button>
              <button
                onClick={fetchResults}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
                <span className="hidden sm:inline">تحديث</span>
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-slate-50/50 border-t border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الترتيب</label>
                      <select
                        value={sortOrder}
                        onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all appearance-none cursor-pointer"
                      >
                        <option value="asc">من الأقدم إلى الأحدث</option>
                        <option value="desc">من الأحدث إلى الأقدم</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">من تاريخ</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
          />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">إلى تاريخ</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 transition-all"
            />
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <div className="mt-3 pt-3 border-t border-slate-200/60">
            <button
                        onClick={() => { setStatusFilter(''); setSortOrder('asc'); setDateFrom(''); setDateTo(''); setCurrentPage(1); }}
                        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
                        <X className="w-3.5 h-3.5" />
                        مسح الفلاتر
            </button>
          </div>
                  )}
        </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

      {/* Bulk Actions */}
        <AnimatePresence>
      {selectedBookings.length > 0 && (
        <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              className="bg-[#1E293B] text-slate-100 rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Check className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <span className="font-medium">
                  تم تحديد <span className="font-bold tabular-nums">{selectedBookings.length}</span> حجز
            </span>
                <button onClick={clearSelection} className="text-slate-400 hover:text-slate-100 text-sm underline underline-offset-2 transition-colors">
              إلغاء التحديد
            </button>
          </div>
              <div className="flex gap-2">
            <button
              onClick={handleApprove}
              disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl text-sm font-medium hover:bg-slate-100 disabled:opacity-50 transition-colors"
            >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4 text-blue-600" strokeWidth={2} />}
              اعتماد
            </button>
            <button
              onClick={handleRelease}
              disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2} />}
              إرسال
            </button>
          </div>
        </motion.div>
      )}
        </AnimatePresence>

      {/* Results Table */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">قائمة الحجوزات</h3>
          <button
            onClick={selectAll}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            تحديد الكل
          </button>
        </div>

        {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-3" />
              <p className="text-sm text-slate-500">جاري تحميل النتائج...</p>
          </div>
        ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">لا توجد نتائج</h3>
              <p className="text-sm text-slate-500">لا توجد نتائج جاهزة للمراجعة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-center px-4 py-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedBookings.length === results.filter(r => r.status !== 'released').length && results.length > 0}
                      onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                        className="w-4 h-4 text-slate-900 rounded border-slate-300 focus:ring-slate-900/20"
                    />
                  </th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الباركود</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم المريض</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التحاليل</th>
                    <th className="text-right px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</th>
                    <th className="text-center px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                    <th className="text-center px-6 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {results.map((booking, index) => (
                  <motion.tr
                    key={booking.booking_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                      className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 ${
                        selectedBookings.includes(booking.booking_id) ? 'bg-slate-50' : ''
                    }`}
                  >
                      <td className="px-4 py-5 text-center">
                      {booking.status !== 'released' && (
                        <input
                          type="checkbox"
                          checked={selectedBookings.includes(booking.booking_id)}
                          onChange={() => toggleSelection(booking.booking_id)}
                            className="w-4 h-4 text-slate-900 rounded border-slate-300 focus:ring-slate-900/20"
                        />
                      )}
                    </td>
                      <td className="px-6 py-5">
                        <span className="font-mono text-sm font-bold text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg">
                        {booking.barcode_no}
                      </span>
                    </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-slate-600">
                              {getPatientName(booking)?.charAt(0) || '؟'}
                            </span>
                        </div>
                          <span className="font-semibold text-slate-900 text-sm">{getPatientName(booking)}</span>
                      </div>
                    </td>
                      <td className="px-6 py-5">
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-lg text-xs font-semibold tabular-nums">
                        {booking.tests_count || 0} تحليل
                      </span>
                    </td>
                      <td className="px-6 py-5 text-sm text-slate-600">{booking.branch_name || '-'}</td>
                      <td className="px-6 py-5 text-center">{getStatusBadge(booking.status)}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleOpenView(booking)}
                            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="عرض التفاصيل"
                        >
                            <Eye className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => navigate(`/lab/report/${booking.booking_id}`)}
                            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="النتيجة الطبية"
                        >
                            <Printer className="w-4 h-4" strokeWidth={2} />
                        </button>
                        {booking.status === 'completed' && (
                          <button
                            onClick={() => handleSingleAction(booking.booking_id, 'approve')}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-150"
                            title="اعتماد"
                          >
                              <Shield className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                        {booking.status === 'approved' && (
                          <button
                            onClick={() => handleSingleAction(booking.booking_id, 'release')}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-150"
                            title="إرسال"
                          >
                              <Send className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          {!loading && results.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/30">
              <p className="text-sm text-slate-600 order-2 sm:order-1">
                عرض <span className="font-semibold text-slate-900">{totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}</span>
                {' - '}
                <span className="font-semibold text-slate-900">{Math.min(currentPage * itemsPerPage, totalItems)}</span>
                {' '}من <span className="font-semibold text-slate-900">{totalItems}</span>
              </p>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm"
                >
                  السابق
                </button>
                <div className="flex items-center gap-1">
                  {visiblePages.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(Math.max(totalPages, 1), p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm"
                >
                  التالي
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <ResultsDetailsModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={viewData}
        title="تفاصيل النتائج"
      />
    </div>
  );
};

export default ApproveResults;
